import React from 'react';
import { Calendar } from 'lucide-react';

interface TimelineControlsProps {
  onMonthsChange: (months: number) => void;
  currentMonths: number;
}

/**
 * TimelineControls Component
 * Provides quick select buttons and custom input for trajectory projection period
 *
 * Features:
 * - Preset buttons (1, 3, 6, 12 months)
 * - Custom month input (1-24 months)
 * - Active state highlighting
 * - Auto-calculate on selection
 */
export const TimelineControls: React.FC<TimelineControlsProps> = ({
  onMonthsChange,
  currentMonths,
}) => {
  const presets = [
    { label: '1 Month', months: 1 },
    { label: '3 Months', months: 3 },
    { label: '6 Months', months: 6 },
    { label: '12 Months', months: 12 },
  ];

  const handleCustomChange = (value: string) => {
    const months = parseInt(value, 10);
    if (!isNaN(months) && months >= 1 && months <= 24) {
      onMonthsChange(months);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Projection Period:</span>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          {/* Preset buttons */}
          <div className="flex gap-2">
            {presets.map((preset) => (
              <button
                key={preset.months}
                onClick={() => onMonthsChange(preset.months)}
                className={`px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium ${
                  currentMonths === preset.months
                    ? 'bg-blue-600 text-white shadow-md scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Custom month selector */}
          <div className="flex items-center gap-2 border-l pl-3 border-gray-300">
            <label className="text-sm text-gray-600 whitespace-nowrap">Custom:</label>
            <input
              type="number"
              min="1"
              max="24"
              value={currentMonths}
              onChange={(e) => handleCustomChange(e.target.value)}
              className="w-16 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-sm text-gray-600">months</span>
          </div>
        </div>
      </div>

      <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
        <span>Select a time period to project future BASIC scores (1-24 months)</span>
      </div>
    </div>
  );
};

export default TimelineControls;
